import { Clear } from '@mui/icons-material'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { type FC, useState } from 'react'

export interface FilterConfig<TFilters, TLookup> {
    key: string
    label: string
    isActive: (f: TFilters) => boolean
    getDisplayValue: (f: TFilters, lookup: TLookup) => string | null
    getTooltipValue?: (f: TFilters, lookup: TLookup) => string | null
    getClearFields: () => Partial<TFilters>
}

export interface ActiveChip {
    key: string
    label: string
    tooltip: string | null
    onDelete: () => void
}

export function buildActiveChips<TFilters, TLookup>(
    configs: FilterConfig<TFilters, TLookup>[],
    filters: TFilters,
    lookupData: TLookup,
    setFilters: (f: TFilters) => void
): ActiveChip[] {
    return configs
        .filter((config) => config.isActive(filters))
        .map((config) => {
            const displayValue = config.getDisplayValue(filters, lookupData)
            if (!displayValue) return null
            const tooltipValue = config.getTooltipValue?.(filters, lookupData)
            return {
                key: config.key,
                label: `${config.label}: ${displayValue}`,
                tooltip: tooltipValue
                    ? `${config.label}: ${tooltipValue}`
                    : null,
                onDelete: () =>
                    setFilters({ ...filters, ...config.getClearFields() }),
            }
        })
        .filter((chip): chip is ActiveChip => chip !== null)
}

export function truncateNames(
    rawNames: (string | undefined | null)[],
    idCount: number
): string | null {
    const names = rawNames.filter((n): n is string => !!n)
    if (names.length === 0) return idCount > 0 ? 'Loading...' : null
    if (names.length <= 2) return names.join(', ')
    return `${names.slice(0, 2).join(', ')} + ${names.length - 2} more`
}

export function tooltipNames(
    rawNames: (string | undefined | null)[]
): string | null {
    const names = rawNames.filter((n): n is string => !!n)
    return names.length > 2 ? names.join(', ') : null
}

const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: '0.6875rem',
    color: 'text.secondary',
    bgcolor: 'grey.100',
    px: 1,
    py: 0.25,
    borderRadius: 1,
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover': { bgcolor: 'grey.200' },
}

const FilterPill: FC<{ chip: ActiveChip }> = ({ chip }) => {
    const [hoverOpen, setHoverOpen] = useState(false)
    const [copiedOpen, setCopiedOpen] = useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(chip.tooltip || chip.label)
        setCopiedOpen(true)
        setTimeout(() => setCopiedOpen(false), 1200)
    }

    const isOpen = copiedOpen || (hoverOpen && !!chip.tooltip)
    const title = copiedOpen ? 'Copied!' : chip.tooltip ?? ''

    return (
        <Tooltip
            title={title}
            open={isOpen}
            onOpen={() => setHoverOpen(true)}
            onClose={() => setHoverOpen(false)}
            arrow
        >
            <Typography component="span" sx={pillSx} onClick={handleCopy}>
                {chip.label}
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation()
                        chip.onDelete()
                    }}
                    sx={{
                        p: '1px',
                        borderRadius: '50%',
                        color: 'inherit',
                        '&:hover': { bgcolor: 'grey.300' },
                    }}
                >
                    <Clear sx={{ fontSize: '0.625rem' }} />
                </IconButton>
            </Typography>
        </Tooltip>
    )
}

export const FilterPills: FC<{ chips: ActiveChip[] }> = ({ chips }) => (
    <>
        {chips.map((chip) => (
            <FilterPill key={chip.key} chip={chip} />
        ))}
    </>
)
